import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Button } from "../../components/button";
import { PageEditor } from "../../components/page-edit";
import { languageFlags } from "../../consts/flags";
import { languageCodes } from "../../consts/language-codes";
import { useGetAssociationContext } from "../../queries/get-association-context";
import { useGetPage } from "../../queries/get-page";
import { useGetPages } from "../../queries/get-pages";
import { useAuthStore } from "../../stores/auth";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";

export function Page() {
  const { slug1, slug2 } = useParams();
  const { data: pages, refetch: refetchPages } = useGetPages();
  const { setLocale, currentLanguage, locale: t } = useLocale();
  const { data: association } = useGetAssociationContext();
  const isAuth = useAuthStore((state) => state.isAuth);

  const [pageId, setPageId] = useState<string>();
  const { data: page, refetch } = useGetPage(pageId);

  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const page = pages?.find(
      (page) => page.slug === `/${slug1}${slug2 ? `/${slug2}` : ""}`,
    );

    if (page) {
      setPageId(page.id);
    }
  }, [slug1, slug2, pages]);

  if (!page || !association) {
    return;
  }

  return (
    <div>
      {editMode && isAuth ? (
        <PageEditor
          cancelButtonText={t.page.cancel}
          page={page}
          submitButtonText={t.page.save}
          onCancel={() => setEditMode(false)}
          onSave={() => {
            (async () => {
              await refetch();
              await refetchPages();
              setEditMode(false);
            })();
          }}
        />
      ) : (
        <>
          <div className="flex justify-between border-b border-gray-200 mb-2">
            <h1 className="text-2xl font-bold">{page.title}</h1>
            <div className="flex items-center gap-2">
              {page.updatedAt && (
                <span className="text-sm text-gray-500">
                  {t.page.updatedAt}: {formatDate(page.updatedAt)}
                </span>
              )}
              {isAuth && (
                <Button
                  link
                  title={t.page.edit}
                  onClick={() => setEditMode(true)}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
              )}
            </div>
          </div>
          {page.type === "roles" ? (
            page.roles && page.roles.length > 0 ? (
              <div className="flex flex-col gap-6">
                {page.roles.map((role) => (
                  <div key={role.id}>
                    <h2 className="text-xl font-semibold mb-1">{role.name}</h2>
                    {role.externalDescription && (
                      <div
                        className="page-content"
                        dangerouslySetInnerHTML={{
                          __html: role.externalDescription,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500">{t.page.noRoles}</div>
            )
          ) : page.content ? (
            <div
              className="page-content"
              dangerouslySetInnerHTML={{ __html: page.content || "" }}
            />
          ) : (
            <>
              {currentLanguage !== association.primaryLanguage ? (
                <div className="text-gray-500">
                  {t.page.noContentInAlternative}{" "}
                  {
                    languageFlags[
                      association.primaryLanguage as keyof typeof languageFlags
                    ]
                  }
                  :{" "}
                  <Button
                    link
                    onClick={() => setLocale(association.primaryLanguage)}
                  >
                    {
                      t.locale[
                        languageCodes[
                          association.primaryLanguage as keyof typeof languageCodes
                        ]
                      ]
                    }
                  </Button>
                </div>
              ) : (
                <div className="text-gray-500">{t.page.noContent}</div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
